/**
 * canvasThemes — 백엔드 services/canvas_themes.py와 동기화된 테마 dict (Phase 1)
 *
 * 동기화 규칙:
 * - 전체 테마(19종) id, colors, radius, padding이 백엔드와 동일
 * - 백엔드가 source of truth — 변경 시 양쪽 동기 업데이트 필수
 * - HtmlSlideRenderer가 :root --wqslide-* 변수에 dynamic injection
 *
 * 사용:
 *   import { getTheme, DEFAULT_THEME_ID, themeCssVars } from '../utils/canvasThemes';
 *   const theme = getTheme(outline.theme);
 *   const vars = themeCssVars(theme);  // ":root { --wqslide-bg: #FFF; ... }" string
 */

export const DEFAULT_THEME_ID = 'toss';

export const THEMES = {
  toss: {
    id: 'toss',
    name: '베이직',
    category: 'professional',
    tone: '친근·신뢰',
    description: '군더더기 없이 깔끔한 기본 스타일',
    isDark: false,
    coverStyle: 'orbit',
    colors: {
      bg: '#FFFFFF',
      bgSubtle: '#F2F4F6',
      bgStrong: '#E5E8EB',
      bgDark: '#191F28',
      text1: '#191F28',
      text2: '#4E5968',
      text3: '#8B95A1',
      textOnDark: '#FFFFFF',
      accent: '#3182F6',
      accentSoft: '#DBEAFE',
      accentStrong: '#2563EB',
      border: '#E5E8EB',
      success: '#059669',
      successSoft: '#D1FAE5',
      successStrong: '#047857',
      warn: '#D97706',
      warnSoft: '#FEF3C7',
      warnStrong: '#B45309',
      info: '#0891B2',
      infoSoft: '#CFFAFE',
      infoStrong: '#077491',
    },
    radius: { card: 20, pill: 999 },
    padding: 80,
  },

  minimal: {
    id: 'minimal',
    name: '미니멀',
    category: 'minimal',
    tone: '절제·여유·고요',
    description: 'Notion 같은 따뜻한 그레이 미니멀 톤',
    isDark: false,
    coverStyle: 'hairline_grid',
    colors: {
      bg: '#FFFFFF',
      bgSubtle: '#FAFAF7',
      bgStrong: '#F0EFEA',
      bgDark: '#2D2D2A',
      text1: '#2D2D2D',
      text2: '#666666',
      text3: '#999999',
      textOnDark: '#FAFAF7',
      accent: '#2D2D2D',
      accentSoft: '#EEEEEE',
      accentStrong: '#000000',
      border: '#E8E6E0',
      success: '#6B7F58',
      successSoft: '#E2E8D8',
      successStrong: '#56684A',
      warn: '#A86E3E',
      warnSoft: '#F0DDC7',
      warnStrong: '#7E5128',
      info: '#5C708A',
      infoSoft: '#D8DEE5',
      infoStrong: '#445466',
    },
    radius: { card: 8, pill: 4 },
    padding: 96,
  },

  academic: {
    id: 'academic',
    name: '아카데믹',
    category: 'editorial',
    tone: '학술·심층·신뢰',
    description: 'WIRED 같은 신문 밀도와 잉크블루 — 학술적·심층 분석',
    isDark: false,
    coverStyle: 'hairline_grid',
    colors: {
      bg: '#FAFAF8',
      bgSubtle: '#F2F0E8',
      bgStrong: '#E4E0D2',
      bgDark: '#181818',
      text1: '#181818',
      text2: '#444444',
      text3: '#888888',
      textOnDark: '#FAFAF8',
      accent: '#003C7E',
      accentSoft: '#DDE5F2',
      accentStrong: '#002958',
      border: '#D4D0C2',
      success: '#46613A',
      successSoft: '#D6DFC9',
      successStrong: '#324228',
      warn: '#A8631F',
      warnSoft: '#F1DDC0',
      warnStrong: '#7E4815',
      info: '#003C7E',
      infoSoft: '#DDE5F2',
      infoStrong: '#002958',
    },
    radius: { card: 4, pill: 4 },
    padding: 72,
  },

  warm: {
    id: 'warm',
    name: '따뜻함',
    category: 'warm',
    tone: '따뜻함·정성·환대',
    description: 'Mastercard 같은 크림 캔버스 + 오렌지 액센트',
    isDark: false,
    coverStyle: 'corner_blob',
    colors: {
      bg: '#FEF8F0',
      bgSubtle: '#FDF1DF',
      bgStrong: '#F5E2C4',
      bgDark: '#2D2418',
      text1: '#1F1A14',
      text2: '#5C544A',
      text3: '#8B7F6C',
      textOnDark: '#FEF8F0',
      accent: '#EA6F3D',
      accentSoft: '#FFE6D3',
      accentStrong: '#C04C1D',
      border: '#EBD8B8',
      success: '#6B8E4E',
      successSoft: '#DCE8CB',
      successStrong: '#4D6936',
      warn: '#EA6F3D',
      warnSoft: '#FFE6D3',
      warnStrong: '#C04C1D',
      info: '#3F6D8E',
      infoSoft: '#D5E2EB',
      infoStrong: '#2A4D66',
    },
    radius: { card: 24, pill: 999 },
    padding: 80,
  },

  dark_pro: {
    id: 'dark_pro',
    name: '다크 프로',
    category: 'dark',
    tone: '전문·세련·집중',
    description: 'Cursor/Stripe 같은 풀 다크 모드',
    isDark: true,
    coverStyle: 'diagonal_band',
    colors: {
      bg: '#0A0A0A',
      bgSubtle: '#1A1A1A',
      bgStrong: '#2D2D2D',
      bgDark: '#000000',
      text1: '#FFFFFF',
      text2: '#B5B5B5',
      text3: '#7F7F7F',
      textOnDark: '#FFFFFF',
      accent: '#6366F1',
      accentSoft: '#1F2138',
      accentStrong: '#4F46E5',
      border: '#2D2D2D',
      success: '#10B981',
      successSoft: '#0F2820',
      successStrong: '#34D399',
      warn: '#F59E0B',
      warnSoft: '#2A1F0A',
      warnStrong: '#FBBF24',
      info: '#06B6D4',
      infoSoft: '#0A2329',
      infoStrong: '#22D3EE',
    },
    radius: { card: 12, pill: 999 },
    padding: 80,
  },

  tech: {
    id: 'tech',
    name: '테크',
    category: 'tech',
    tone: '정밀·체계·집중',
    description: 'Linear/Vercel 같은 모노톤 정밀함',
    isDark: false,
    coverStyle: 'dot_field',
    colors: {
      bg: '#FFFFFF',
      bgSubtle: '#F4F4F5',
      bgStrong: '#E4E4E7',
      bgDark: '#18181B',
      text1: '#18181B',
      text2: '#52525B',
      text3: '#A1A1AA',
      textOnDark: '#FAFAFA',
      accent: '#6366F1',
      accentSoft: '#E0E7FF',
      accentStrong: '#4338CA',
      border: '#E4E4E7',
      success: '#10B981',
      successSoft: '#D1FAE5',
      successStrong: '#047857',
      warn: '#F59E0B',
      warnSoft: '#FEF3C7',
      warnStrong: '#B45309',
      info: '#3B82F6',
      infoSoft: '#DBEAFE',
      infoStrong: '#1D4ED8',
    },
    radius: { card: 8, pill: 6 },
    padding: 80,
  },

  energy: {
    id: 'energy',
    name: '에너지',
    category: 'dark',
    tone: '에너지·미래·자극',
    description: 'MiniMax 같은 다크 + 네온 — 중·고등 적합',
    isDark: true,
    coverStyle: 'diagonal_band',
    colors: {
      bg: '#0F0F1A',
      bgSubtle: '#1A1A2E',
      bgStrong: '#252544',
      bgDark: '#080812',
      text1: '#FFFFFF',
      text2: '#A5A5C0',
      text3: '#7575A0',
      textOnDark: '#FFFFFF',
      accent: '#00FFB2',
      accentSoft: '#0F4838',
      accentStrong: '#00D896',
      border: '#252544',
      success: '#00FFB2',
      successSoft: '#0F4838',
      successStrong: '#00D896',
      warn: '#FFD93D',
      warnSoft: '#3D360F',
      warnStrong: '#E0BC1F',
      info: '#00C0FF',
      infoSoft: '#0F2A3D',
      infoStrong: '#00A0DD',
    },
    radius: { card: 16, pill: 999 },
    padding: 80,
  },

  calm: {
    id: 'calm',
    name: '차분함',
    category: 'professional',
    tone: '차분·구조·신뢰',
    description: 'IBM Carbon 같은 차분한 구조화 — 공공·기업 보고',
    isDark: false,
    coverStyle: 'corner_blob',
    colors: {
      bg: '#FFFFFF',
      bgSubtle: '#F4F4F4',
      bgStrong: '#E0E0E0',
      bgDark: '#161616',
      text1: '#161616',
      text2: '#525252',
      text3: '#A8A8A8',
      textOnDark: '#F4F4F4',
      accent: '#0F62FE',
      accentSoft: '#D0E2FF',
      accentStrong: '#0043CE',
      border: '#E0E0E0',
      success: '#24A148',
      successSoft: '#DEFBE6',
      successStrong: '#1A7032',
      warn: '#F1C21B',
      warnSoft: '#FCF4D6',
      warnStrong: '#C89C18',
      info: '#0F62FE',
      infoSoft: '#D0E2FF',
      infoStrong: '#0043CE',
    },
    radius: { card: 0, pill: 0 },
    padding: 72,
  },

  nature: {
    id: 'nature',
    name: '자연',
    category: 'nature',
    tone: '자연·생명·평온',
    description: 'Mintlify 같은 그린 액센트 — 환경·생명·식물 주제',
    isDark: false,
    coverStyle: 'corner_blob',
    colors: {
      bg: '#FFFFFF',
      bgSubtle: '#ECFDF5',
      bgStrong: '#D1FAE5',
      bgDark: '#022C22',
      text1: '#064E3B',
      text2: '#047857',
      text3: '#6B7F75',
      textOnDark: '#ECFDF5',
      accent: '#10B981',
      accentSoft: '#D1FAE5',
      accentStrong: '#047857',
      border: '#D1FAE5',
      success: '#10B981',
      successSoft: '#D1FAE5',
      successStrong: '#047857',
      warn: '#F59E0B',
      warnSoft: '#FEF3C7',
      warnStrong: '#B45309',
      info: '#0891B2',
      infoSoft: '#CFFAFE',
      infoStrong: '#077491',
    },
    radius: { card: 16, pill: 999 },
    padding: 80,
  },

  // ══════════════════════════════════════════════════════════════════
  // Phase 3 확장 — pptx 스킬 검증 팔레트 10종 (9 → 19)
  // 백엔드 services/canvas_themes.py와 동기 — 변경 시 양쪽 함께 수정
  // ══════════════════════════════════════════════════════════════════

  midnight: {
    id: 'midnight',
    name: '미드나잇',
    category: 'dark',
    tone: '중후·격식·프리미엄',
    description: '딥 네이비 + 아이스 블루 — 격식 있는 프리미엄 보고',
    isDark: true,
    coverStyle: 'dot_field',
    colors: {
      bg: '#141A3C',
      bgSubtle: '#1E2761',
      bgStrong: '#2A3578',
      bgDark: '#0A0E24',
      text1: '#FFFFFF',
      text2: '#C3CDE8',
      text3: '#8E9AC4',
      textOnDark: '#FFFFFF',
      accent: '#5B82D6',
      accentSoft: '#27316B',
      accentStrong: '#CADCFC',
      border: '#2A3578',
      success: '#34D399',
      successSoft: '#103D2E',
      successStrong: '#6EE7B7',
      warn: '#FBBF24',
      warnSoft: '#3D2F0A',
      warnStrong: '#FCD34D',
      info: '#7DAFFF',
      infoSoft: '#16224F',
      infoStrong: '#A5C8FF',
    },
    radius: { card: 12, pill: 999 },
    padding: 80,
  },

  forest: {
    id: 'forest',
    name: '포레스트',
    category: 'nature',
    tone: '숲·안정·생명력',
    description: '짙은 포레스트 그린 + 모스 — nature보다 깊고 차분한 숲 톤',
    isDark: false,
    coverStyle: 'hairline_grid',
    colors: {
      bg: '#F9FBF4',
      bgSubtle: '#EEF3E2',
      bgStrong: '#DCE5C8',
      bgDark: '#1E3A1F',
      text1: '#1E3A1F',
      text2: '#44603F',
      text3: '#7C8F74',
      textOnDark: '#F5F5F5',
      accent: '#2C5F2D',
      accentSoft: '#DFEAD2',
      accentStrong: '#1E4520',
      border: '#DCE5C8',
      success: '#2C5F2D',
      successSoft: '#DFEAD2',
      successStrong: '#1E4520',
      warn: '#C07A28',
      warnSoft: '#F4E3C8',
      warnStrong: '#92591C',
      info: '#3F6D8E',
      infoSoft: '#D9E5EE',
      infoStrong: '#2A4D66',
    },
    radius: { card: 16, pill: 999 },
    padding: 80,
  },

  coral: {
    id: 'coral',
    name: '코랄',
    category: 'warm',
    tone: '활기·즐거움·생동감',
    description: '코랄 레드 + 골드 포인트 — 밝고 에너지 넘치는 초등 친화 톤',
    isDark: false,
    coverStyle: 'orbit',
    colors: {
      bg: '#FFFFFF',
      bgSubtle: '#FFF3F0',
      bgStrong: '#FFDFD6',
      bgDark: '#2F3C7E',
      text1: '#26284A',
      text2: '#54577A',
      text3: '#9093B0',
      textOnDark: '#FFFFFF',
      accent: '#F96167',
      accentSoft: '#FFE2E0',
      accentStrong: '#E03F46',
      border: '#FFDFD6',
      success: '#2E9E6B',
      successSoft: '#D9F2E5',
      successStrong: '#1F7A50',
      warn: '#DB9A18',
      warnSoft: '#FBEFCE',
      warnStrong: '#A87410',
      info: '#2F3C7E',
      infoSoft: '#E0E3F4',
      infoStrong: '#222C61',
    },
    radius: { card: 20, pill: 999 },
    padding: 80,
  },

  terracotta: {
    id: 'terracotta',
    name: '테라코타',
    category: 'warm',
    tone: '흙내음·고풍·운치',
    description: '테라코타 + 샌드 베이지 — 미술·역사·인문에 어울리는 어스 톤',
    isDark: false,
    coverStyle: 'diagonal_band',
    colors: {
      bg: '#FBF8EF',
      bgSubtle: '#F2EFDE',
      bgStrong: '#E7E8D1',
      bgDark: '#3B2A26',
      text1: '#3B2A26',
      text2: '#6E5A52',
      text3: '#9C8B82',
      textOnDark: '#FBF8EF',
      accent: '#B85042',
      accentSoft: '#F1DCD8',
      accentStrong: '#93392E',
      border: '#E7E8D1',
      success: '#5F7E63',
      successSoft: '#DFE9E0',
      successStrong: '#48624C',
      warn: '#C07A28',
      warnSoft: '#F4E3C8',
      warnStrong: '#92591C',
      info: '#56707E',
      infoSoft: '#DEE7EC',
      infoStrong: '#3F555F',
    },
    radius: { card: 18, pill: 999 },
    padding: 80,
  },

  ocean: {
    id: 'ocean',
    name: '오션',
    category: 'professional',
    tone: '깊이·탐구·시원함',
    description: '딥 오션 블루 + 틸 그라데이션 — 바다·지구과학·탐구 주제',
    isDark: false,
    coverStyle: 'dot_field',
    colors: {
      bg: '#F4F9FC',
      bgSubtle: '#E4F0F7',
      bgStrong: '#C9E1EE',
      bgDark: '#21295C',
      text1: '#122B3C',
      text2: '#3A5568',
      text3: '#7591A3',
      textOnDark: '#F4F9FC',
      accent: '#065A82',
      accentSoft: '#D4E7F1',
      accentStrong: '#21295C',
      border: '#C9E1EE',
      success: '#1F8A70',
      successSoft: '#D5EEE7',
      successStrong: '#166854',
      warn: '#C58A2A',
      warnSoft: '#F5E8CD',
      warnStrong: '#96671E',
      info: '#1C7293',
      infoSoft: '#D9EAF1',
      infoStrong: '#14546D',
    },
    radius: { card: 16, pill: 999 },
    padding: 80,
  },

  charcoal: {
    id: 'charcoal',
    name: '차콜',
    category: 'minimal',
    tone: '단정·모던·간결',
    description: '차콜 그레이 모노톤 — minimal보다 차갑고 단단한 정리 톤',
    isDark: false,
    coverStyle: 'hairline_grid',
    colors: {
      bg: '#FFFFFF',
      bgSubtle: '#F2F2F2',
      bgStrong: '#E0E2E4',
      bgDark: '#212121',
      text1: '#212121',
      text2: '#4B565E',
      text3: '#8A949B',
      textOnDark: '#F2F2F2',
      accent: '#36454F',
      accentSoft: '#E3E8EB',
      accentStrong: '#212121',
      border: '#E0E2E4',
      success: '#3E7C59',
      successSoft: '#DDEBE3',
      successStrong: '#2E5D43',
      warn: '#A8772B',
      warnSoft: '#F0E5CF',
      warnStrong: '#7E591F',
      info: '#46697E',
      infoSoft: '#DCE6EC',
      infoStrong: '#334E5E',
    },
    radius: { card: 6, pill: 6 },
    padding: 88,
  },

  teal: {
    id: 'teal',
    name: '틸',
    category: 'professional',
    tone: '신뢰·돌봄·청량',
    description: '틸 + 민트 그라데이션 — 보건·상담·돌봄 주제의 신뢰 톤',
    isDark: false,
    coverStyle: 'orbit',
    colors: {
      bg: '#FFFFFF',
      bgSubtle: '#EBF7F7',
      bgStrong: '#CFEDEC',
      bgDark: '#0D3B40',
      text1: '#0D3B40',
      text2: '#34626A',
      text3: '#73989E',
      textOnDark: '#EBF7F7',
      accent: '#028090',
      accentSoft: '#D2EDF0',
      accentStrong: '#02606C',
      border: '#CFEDEC',
      success: '#02C39A',
      successSoft: '#D4F5EC',
      successStrong: '#029273',
      warn: '#D08C24',
      warnSoft: '#F7E9CC',
      warnStrong: '#9C691B',
      info: '#00A896',
      infoSoft: '#D3F1EE',
      infoStrong: '#007D70',
    },
    radius: { card: 16, pill: 999 },
    padding: 80,
  },

  berry: {
    id: 'berry',
    name: '베리',
    category: 'editorial',
    tone: '감성·우아·깊이',
    description: '베리 와인 + 크림 — 문학·예술에 어울리는 감성 톤',
    isDark: false,
    coverStyle: 'corner_blob',
    colors: {
      bg: '#FAF5EC',
      bgSubtle: '#F2EADC',
      bgStrong: '#ECE2D0',
      bgDark: '#43202F',
      text1: '#43202F',
      text2: '#6E4A56',
      text3: '#9D8089',
      textOnDark: '#FAF5EC',
      accent: '#6D2E46',
      accentSoft: '#EBD9DF',
      accentStrong: '#4E1F32',
      border: '#ECE2D0',
      success: '#5F7E63',
      successSoft: '#DFE9E0',
      successStrong: '#48624C',
      warn: '#B97C2A',
      warnSoft: '#F2E4CB',
      warnStrong: '#8B5C1E',
      info: '#566F8C',
      infoSoft: '#DEE6EF',
      infoStrong: '#3F5369',
    },
    radius: { card: 14, pill: 999 },
    padding: 84,
  },

  sage: {
    id: 'sage',
    name: '세이지',
    category: 'nature',
    tone: '안온·치유·균형',
    description: '세이지 그린 + 슬레이트 — 명상·치유 같은 잔잔한 톤',
    isDark: false,
    coverStyle: 'dot_field',
    colors: {
      bg: '#F7FAF8',
      bgSubtle: '#EAF2ED',
      bgStrong: '#D5E4DB',
      bgDark: '#2C4047',
      text1: '#2C4047',
      text2: '#50676D',
      text3: '#85999C',
      textOnDark: '#F7FAF8',
      accent: '#50808E',
      accentSoft: '#DCE8EC',
      accentStrong: '#3A626F',
      border: '#D5E4DB',
      success: '#69A297',
      successSoft: '#E0EEEB',
      successStrong: '#4F7E75',
      warn: '#B98A2E',
      warnSoft: '#F2E7CD',
      warnStrong: '#8B6722',
      info: '#50808E',
      infoSoft: '#DCE8EC',
      infoStrong: '#3A626F',
    },
    radius: { card: 18, pill: 999 },
    padding: 88,
  },

  cherry: {
    id: 'cherry',
    name: '체리',
    category: 'editorial',
    tone: '강렬·확신·임팩트',
    description: '체리 레드 + 네이비 — 주장이 분명한 발표·토론용 임팩트 톤',
    isDark: false,
    coverStyle: 'diagonal_band',
    colors: {
      bg: '#FCF6F5',
      bgSubtle: '#F6EAE8',
      bgStrong: '#EDD9D6',
      bgDark: '#1C1416',
      text1: '#1C1416',
      text2: '#54454A',
      text3: '#8E7C81',
      textOnDark: '#FCF6F5',
      accent: '#990011',
      accentSoft: '#F2D7DA',
      accentStrong: '#6E000C',
      border: '#EDD9D6',
      success: '#3E7C59',
      successSoft: '#DDEBE3',
      successStrong: '#2E5D43',
      warn: '#B97C2A',
      warnSoft: '#F2E4CB',
      warnStrong: '#8B5C1E',
      info: '#2F3C7E',
      infoSoft: '#E0E3F4',
      infoStrong: '#222C61',
    },
    radius: { card: 10, pill: 999 },
    padding: 80,
  },
};

/** theme_id로 테마 dict 반환. 없으면 toss. */
export function getTheme(themeId) {
  if (!themeId || !THEMES[themeId]) {
    return THEMES[DEFAULT_THEME_ID];
  }
  return THEMES[themeId];
}

/** 모든 테마의 메타 (UI 드롭다운용) */
export function listThemes() {
  return Object.values(THEMES).map((t) => ({
    id: t.id,
    name: t.name,
    category: t.category,
    tone: t.tone,
    description: t.description,
    isDark: t.isDark,
    previewColors: {
      bg: t.colors.bg,
      accent: t.colors.accent,
      bgSubtle: t.colors.bgSubtle,
      text1: t.colors.text1,
    },
  }));
}

/**
 * theme dict → HtmlSlideRenderer :root CSS 변수 문자열
 *
 * 예: themeCssVars(getTheme('warm'))
 *  → "--wqslide-bg: #FEF8F0; --wqslide-text-1: #1F1A14; ..."
 */
export function themeCssVars(theme) {
  const c = theme.colors;
  return [
    `--wqslide-bg: ${c.bg}`,
    `--wqslide-bg-subtle: ${c.bgSubtle}`,
    `--wqslide-bg-strong: ${c.bgStrong}`,
    `--wqslide-text-1: ${c.text1}`,
    `--wqslide-text-2: ${c.text2}`,
    `--wqslide-text-3: ${c.text3}`,
    `--wqslide-accent: ${c.accent}`,
    `--wqslide-accent-soft: ${c.accentSoft}`,
    `--wqslide-accent-strong: ${c.accentStrong}`,
    `--wqslide-border: ${c.border}`,
    `--wqslide-success: ${c.success}`,
    `--wqslide-success-soft: ${c.successSoft}`,
    `--wqslide-success-strong: ${c.successStrong}`,
    `--wqslide-warn: ${c.warn}`,
    `--wqslide-warn-soft: ${c.warnSoft}`,
    `--wqslide-warn-strong: ${c.warnStrong}`,
    `--wqslide-info: ${c.info}`,
    `--wqslide-info-soft: ${c.infoSoft}`,
    `--wqslide-info-strong: ${c.infoStrong}`,
    `--wqslide-radius: ${theme.radius.card}px`,
    `--wqslide-radius-pill: ${theme.radius.pill}px`,
    `--wqslide-padding: ${theme.padding}px`,
  ].join('; ');
}
